import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Table,
  Badge,
  SimpleGrid,
  Stack,
  Group,
  Loader,
  Alert,
} from '@mantine/core';
import api from '@/config/api';

interface FinalPayItem {
  description: string;
  amount: number;
}

interface FinalPayData {
  status: string;
  termination_date: string;
  final_pay_deadline: string;
  final_pay_amount: number;
  cobra_status: string | null;
  cobra_election_deadline: string | null;
  items: FinalPayItem[];
}

function fmt(val: number): string {
  return `$${parseFloat(String(val || 0)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function statusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'green';
    case 'processing':
      return 'blue';
    case 'pending':
    default:
      return 'orange';
  }
}

function cobraColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'elected':
      return 'green';
    case 'waived':
      return 'red';
    case 'pending':
    default:
      return 'orange';
  }
}

export default function FinalPayStatus() {
  const [data, setData] = useState<FinalPayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get('/payroll/my/final-pay-status')
      .then((res) => setData(res.data || null))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader mx="auto" mt="xl" />;
  if (error) return <Alert color="red" title="Error">{error}</Alert>;

  if (!data || !data.termination_date) {
    return (
      <Container size="lg">
        <Title order={2} mb="lg">Final Pay Status</Title>
        <Alert color="blue">No termination records found. This page is only applicable if you have a pending or completed termination.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Final Pay Status</Title>
        <Badge size="lg" variant="filled" color={statusColor(data.status)}>
          {data.status}
        </Badge>
      </Group>

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="lg">
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">Termination Date</Text>
          <Text size="lg" fw={700}>{data.termination_date}</Text>
        </Paper>
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">Final Pay Deadline</Text>
          <Text size="lg" fw={700}>{data.final_pay_deadline}</Text>
        </Paper>
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">Total Amount</Text>
          <Text size="lg" fw={700} c="green">{fmt(data.final_pay_amount)}</Text>
        </Paper>
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">COBRA Status</Text>
          {data.cobra_status ? (
            <Badge size="lg" variant="light" color={cobraColor(data.cobra_status)}>
              {data.cobra_status}
            </Badge>
          ) : (
            <Text size="lg" c="dimmed">N/A</Text>
          )}
        </Paper>
      </SimpleGrid>

      {/* Itemized Final Pay Table */}
      <Stack gap="lg">
        <Paper shadow="xs" radius="md" style={{ overflow: 'auto' }}>
          <Title order={4} p="md" pb={0}>Final Pay Components</Title>
          {data.items && data.items.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.items.map((item, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td>{item.description}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }} fw={500}>
                      {fmt(item.amount)}
                    </Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td fw={700}>Total</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }} fw={700} c="green">
                    {fmt(data.final_pay_amount)}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          ) : (
            <Text p="md" c="dimmed">No itemized breakdown available.</Text>
          )}
        </Paper>

        {/* COBRA Info Card */}
        {data.cobra_status && (
          <Paper p="md" shadow="xs" radius="md">
            <Title order={4} mb="sm">COBRA Continuation Coverage</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">COBRA Status</Text>
                <Badge size="lg" variant="light" color={cobraColor(data.cobra_status)}>
                  {data.cobra_status}
                </Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Election Deadline</Text>
                <Text fw={600}>
                  {data.cobra_election_deadline || 'N/A'}
                </Text>
              </div>
            </SimpleGrid>
            {data.cobra_status?.toLowerCase() === 'pending' && (
              <Alert color="yellow" mt="md" variant="light">
                Your COBRA election deadline is approaching. Please contact HR to make your election
                before {data.cobra_election_deadline || 'the deadline'}.
              </Alert>
            )}
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
